import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, AlertTriangle, ShieldCheck } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { couleurGain, NEUTRE } from '../../lib/couleurs';
import {
  fetchRisqueHuile,
  fetchDettesConversion,
  fetchVentesDecouvert,
  rapprochementDroits,
  annulerConversion,
  FAMILLES,
} from '../../lib/risqueHuile';
import { ConversionDialog } from './ConversionDialog';
import { ReglerConversionDialog } from './ReglerConversionDialog';
import { useAuth } from '../../lib/auth';

const th = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500';
const td = 'px-3 py-2 text-xs';
const champ =
  'mt-1 h-9 w-full border border-slate-300 bg-slate-50 px-2 text-right font-data text-xs outline-none focus:border-slate-900 focus:bg-white';

const STATUT = {
  rouge: { cadre: 'border-l-red-600', fond: 'bg-red-50', texte: 'text-red-800', mot: 'Besoin supérieur à la capacité financière' },
  ambre: { cadre: 'border-l-amber-500', fond: 'bg-amber-50', texte: 'text-amber-900', mot: 'Marge de sécurité sous le seuil' },
  vert: { cadre: 'border-l-emerald-600', fond: 'bg-emerald-50', texte: 'text-emerald-800', mot: 'Couverture financière suffisante' },
  incomplet: { cadre: 'border-l-slate-400', fond: 'bg-slate-100', texte: 'text-slate-700', mot: 'Calcul incomplet — prix de remplacement manquant' },
};

const Ind = ({ titre, valeur, unite, detail, couleur = NEUTRE, testid }) => (
  <div data-testid={testid} className="bg-white px-4 py-3">
    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
    <p className={`tabular font-data text-xl font-medium ${couleur}`}>
      {valeur} {unite && <span className="text-xs text-slate-500">{unite}</span>}
    </p>
    {detail && <p className="font-data text-[11px] text-slate-500">{detail}</p>}
  </div>
);

// Bloc « Risque huile & Crash-Test » : stock physique, droits clients, découvert,
// coût de reconstitution et capacité financière mobilisable.
export const RisqueHuile = () => {
  const { user } = useAuth();
  const [h, setH] = useState({
    prixNoble: '',
    prixLampante: '',
    haussePct: 15,
    fraisPct: 0,
    reserve: 0,
    engagements: 0,
    creditsMobilisables: 0,
    inclureCredits: false,
    seuilSecurite: 10,
    venteSimuleeKg: 0,
    venteSimuleeFamille: 'noble',
  });
  const [r, setR] = useState(null);
  const [dettes, setDettes] = useState([]);
  const [ventes, setVentes] = useState([]);
  const [ecarts, setEcarts] = useState([]);
  const [conversion, setConversion] = useState(false);
  const [aRegler, setAregler] = useState(null);

  const load = useCallback(async () => {
    setR(await fetchRisqueHuile(h));
    setDettes(fetchDettesConversion());
    setVentes(fetchVentesDecouvert());
    setEcarts(rapprochementDroits());
  }, [h]);
  useEffect(() => { load(); }, [load]);

  if (!r) return null;
  const s = STATUT[r.statut];
  const decouvertTotal = r.familles.reduce((a, f) => a + f.decouvert, 0);

  return (
    <section data-testid="risque-huile" className={`border border-slate-200 border-l-2 ${s.cadre} bg-white`}>
      <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            <ShieldCheck size={16} className="text-slate-700" /> Risque huile & Crash-Test
          </p>
          <p className="text-xs text-slate-500">
            Stock physique, droits clients, découvert en nature et capacité à reconstituer — les ventes sur
            l'huile en garde restent autorisées, les alertes ne bloquent jamais une opération.
          </p>
        </div>
        <button
          type="button"
          data-testid="ouvrir-conversion"
          onClick={() => setConversion(true)}
          className="flex h-10 items-center gap-2 border-2 border-amber-600 bg-amber-50 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-amber-900 hover:bg-amber-600 hover:text-white"
        >
          <Coins size={14} /> Conversion huile → dette
        </button>
      </div>

      <p data-testid="risque-statut" className={`flex items-start gap-2 px-4 py-2 text-xs font-bold ${s.fond} ${s.texte}`}>
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        {s.mot} — besoin {fmt(r.besoin, 3)} TND vs capacité {fmt(r.capacite, 3)} TND (
        {r.soldeFinancement >= 0 ? 'excédent' : 'déficit'} {fmt(Math.abs(r.soldeFinancement), 3)} TND).
        Une trésorerie suffisante ne garantit PAS la disponibilité physique de l'huile dans le délai demandé.
      </p>

      <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        <Ind testid="risque-physique" titre="Stock physique mobilisable" valeur={fmt(r.familles.reduce((a, f) => a + f.physique, 0), 3)} unite="kg" detail={r.familles.map((f) => `${f.label} : ${fmt(f.physique, 1)}`).join(' · ')} />
        <Ind testid="risque-droits" titre="Total des droits clients" valeur={fmt(r.familles.reduce((a, f) => a + f.droitsKg, 0), 3)} unite="kg" detail={r.familles.map((f) => `${f.label} : ${fmt(f.droitsKg, 1)}`).join(' · ')} />
        <Ind testid="risque-decouvert" titre="Découvert en huile" valeur={fmt(decouvertTotal, 3)} unite="kg" couleur={couleurGain(-decouvertTotal)} detail={r.familles.map((f) => `${f.label} : ${fmt(f.decouvert, 1)}`).join(' · ')} />
        <Ind testid="risque-couverture" titre="Taux de couverture physique" valeur={r.familles.map((f) => `${fmt(f.couverture, 1)} %`).join(' / ')} detail={r.familles.map((f) => f.label).join(' / ')} />
        <Ind testid="risque-prix" titre="Prix de remplacement" valeur={r.familles.map((f) => fmt(f.prixRef, 3)).join(' / ')} unite="TND/kg" detail={`cours ERP du ${r.cours.le ? String(r.cours.le).slice(0, 16).replace('T', ' ') : 'jamais saisi'} — distinct du CMP historique`} />
        <Ind testid="risque-cout" titre="Coût total de reconstitution" valeur={fmt(r.besoin, 3)} unite="TND" detail={`dont ${fmt(r.dettesConversion, 3)} TND de conversions impayées`} />
        <Ind testid="risque-capacite" titre="Capacité financière mobilisable" valeur={fmt(r.capacite, 3)} unite="TND" detail={`liquidités ${fmt(r.liquidites, 3)} − réserve ${fmt(r.reserve, 3)} − engagements ${fmt(r.engagements, 3)}${r.inclureCredits ? ` + crédits ${fmt(r.creditsMobilisables, 3)}` : ''}`} />
        <Ind testid="risque-solde" titre={r.soldeFinancement >= 0 ? 'Excédent de financement' : 'Déficit de financement'} valeur={fmt(Math.abs(r.soldeFinancement), 3)} unite="TND" couleur={couleurGain(r.soldeFinancement)} detail={`scénario hausse +${h.haussePct} % : ${fmt(r.soldeStresse, 3)} TND`} />
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-slate-200 px-4 py-3 sm:grid-cols-4 lg:grid-cols-6">
        {[
          ['prixNoble', 'Prix Extra/Vierge', r.cours.noble],
          ['prixLampante', 'Prix Lampante', r.cours.lampante],
          ['haussePct', 'Hausse scénario (%)'],
          ['fraisPct', 'Frais retenus (%)'],
          ['reserve', 'Réserve de trésorerie'],
          ['engagements', 'Sommes engagées'],
          ['creditsMobilisables', 'Crédits confirmés'],
          ['seuilSecurite', 'Seuil de sécurité (%)'],
          ['venteSimuleeKg', 'Vente simulée (kg)'],
        ].map(([k, lib, defaut]) => (
          <label key={k}>
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{lib}</span>
            <input
              data-testid={`hypothese-${k}`}
              type="number"
              step="0.001"
              value={h[k]}
              placeholder={defaut != null ? String(defaut) : ''}
              onChange={(e) => setH({ ...h, [k]: e.target.value })}
              className={champ}
            />
          </label>
        ))}
        <label className="flex items-end gap-2 pb-1">
          <input data-testid="hypothese-inclureCredits" type="checkbox" checked={h.inclureCredits} onChange={(e) => setH({ ...h, inclureCredits: e.target.checked })} className="h-4 w-4" />
          <span className="text-[10px] font-bold uppercase text-slate-600">Inclure les crédits</span>
        </label>
      </div>

      {Number(h.venteSimuleeKg) > 0 && (
        <p data-testid="impact-vente" className="border-t border-slate-200 bg-slate-50 px-4 py-2 font-data text-xs text-slate-700">
          Impact prévisionnel d'une vente de {fmt(Number(h.venteSimuleeKg), 1)} kg ({h.venteSimuleeFamille}) : découvert{' '}
          {fmt(r.familles.reduce((a, f) => a + f.decouvertApres, 0), 1)} kg · besoin {fmt(r.besoinApres, 3)} TND ·{' '}
          <span className={couleurGain(r.soldeApresVente)}>
            {r.soldeApresVente >= 0 ? 'excédent' : 'déficit'} {fmt(Math.abs(r.soldeApresVente), 3)} TND
          </span>
          . Vente autorisée : l'alerte est informative et la décision du gérant est journalisée.
        </p>
      )}

      {ecarts.some((e) => Math.abs(e.ecart) > 0.001) && (
        <p data-testid="ecart-reprise" className="border-t border-slate-200 bg-amber-50 px-4 py-2 font-data text-[11px] text-amber-900">
          Écart de rapprochement des droits (recalcul depuis les opérations d'origine vs dette en nature portée par les cuves) :{' '}
          {ecarts.filter((e) => Math.abs(e.ecart) > 0.001).map((e) => `${e.label} ${e.ecart > 0 ? '+' : ''}${fmt(e.ecart, 3)} kg`).join(' · ')}
        </p>
      )}

      {r.droits.length > 0 && (
        <div className="overflow-x-auto border-t border-slate-200">
          <table className="w-full border-collapse" data-testid="table-droits">
            <thead className="bg-slate-50">
              <tr>{['Client', 'Qualité', 'Déposé (kg)', 'Restitué (kg)', 'Converti (kg)', 'Droit restant (kg)'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {r.droits.map((d) => (
                <tr key={d.cle} data-testid={`droit-${d.cle}`}>
                  <td className={`${td} font-semibold text-slate-900`}>{d.nom}</td>
                  <td className={td}>{FAMILLES.find((f) => f.id === d.famille)?.label}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(d.depose, 3)}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(d.retire, 3)}</td>
                  <td className={`${td} tabular text-right font-data text-amber-800`}>{d.converti ? fmt(d.converti, 3) : '—'}</td>
                  <td className={`${td} tabular text-right font-data font-bold text-slate-900`}>{fmt(d.droitKg, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dettes.length > 0 && (
        <div className="overflow-x-auto border-t border-slate-200">
          <p className="px-4 py-2 font-heading text-xs font-black uppercase tracking-[0.08em] text-slate-700">
            Conversions huile → dette monétaire
          </p>
          <table className="w-full border-collapse" data-testid="table-conversions">
            <thead className="bg-slate-50">
              <tr>{['Réf.', 'Date effet', 'Client', 'Qualité', 'Kg', 'Prix', 'Montant', 'Réglé', 'Reste', 'Échéance', 'Statut', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dettes.map((d) => (
                <tr key={d.id} data-testid={`conversion-${d.id}`}>
                  <td className={`${td} font-data font-semibold`}>{d.id}</td>
                  <td className={`${td} font-data`}>{d.dateEffet}</td>
                  <td className={td}>{d.client}</td>
                  <td className={td}>{FAMILLES.find((f) => f.id === d.famille)?.label}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(d.kg, 3)}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(d.prixKg, 3)}</td>
                  <td className={`${td} tabular text-right font-data font-bold`}>{fmt(d.montant, 3)}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(d.paye, 3)}</td>
                  <td className={`${td} tabular text-right font-data font-bold ${couleurGain(-d.reste)}`}>{fmt(d.reste, 3)}</td>
                  <td className={`${td} font-data`}>{d.echeance || '—'}</td>
                  <td className={`${td} text-[10px] font-bold uppercase`}>
                    {d.effetAuPaiement && !d.payeeLe ? 'effet au paiement' : d.reste <= 0.001 ? 'soldée' : 'à payer'}
                  </td>
                  <td className={`${td} text-right`}>
                    <div className="flex justify-end gap-1">
                      {d.reste > 0.001 && (
                        <button
                          type="button"
                          data-testid={`regler-conversion-${d.id}`}
                          onClick={() => setAregler(d)}
                          className="border border-slate-300 px-2 py-1 text-[10px] font-bold text-slate-700 hover:bg-slate-900 hover:text-white"
                        >
                          Régler
                        </button>
                      )}
                      {d.paye <= 0.001 && (
                        <button
                          type="button"
                          data-testid={`annuler-conversion-${d.id}`}
                          onClick={async () => {
                            const motif = window.prompt('Motif d’annulation (traçé dans le journal) :', '');
                            if (motif === null) return;
                            const { error } = await annulerConversion({ id: d.id, motif, utilisateur: user?.name });
                            if (error) return toast.error(error);
                            toast.success('Conversion annulée — les kilos sont restitués au compte huile');
                            load();
                          }}
                          className="border border-slate-300 px-2 py-1 text-[10px] font-bold text-slate-500 hover:bg-red-600 hover:text-white"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ventes.length > 0 && (
        <div className="overflow-x-auto border-t border-slate-200">
          <p className="px-4 py-2 font-heading text-xs font-black uppercase tracking-[0.08em] text-slate-700">
            Ventes ayant creusé le découvert en huile
          </p>
          <table className="w-full border-collapse" data-testid="table-ventes-decouvert">
            <thead className="bg-slate-50">
              <tr>{['Date', 'Vente', 'Client', 'Huile', 'Dont découvert', 'Montant', 'Paiement', 'Encaissé'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ventes.map((v) => (
                <tr key={v.id} data-testid={`vente-decouvert-${v.id}`}>
                  <td className={`${td} font-data`}>{v.date}</td>
                  <td className={`${td} font-data font-semibold`}>{v.id}</td>
                  <td className={td}>{v.client}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(v.kg, 1)}</td>
                  <td className={`${td} tabular text-right font-data font-bold text-red-700`}>{fmt(v.kgDecouvert, 1)}</td>
                  <td className={`${td} tabular text-right font-data`}>{fmt(v.total, 3)}</td>
                  <td className={`${td} text-[10px] font-bold uppercase`}>{v.paiement}</td>
                  <td className={`${td} tabular text-right font-data ${couleurGain(v.encaisse)}`}>{fmt(v.encaisse, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 font-data text-[10px] text-slate-500">
            Une vente à crédit ne génère de trésorerie qu'à son encaissement : la colonne « Encaissé » distingue le
            réalisé du prévisionnel. Créances clients et productions futures ne sont PAS comptées dans la capacité mobilisable.
          </p>
        </div>
      )}

      {conversion && <ConversionDialog onClose={() => setConversion(false)} onDone={load} />}
      {aRegler && (
        <ReglerConversionDialog
          dette={aRegler}
          utilisateur={user?.name}
          onClose={() => setAregler(null)}
          onDone={load}
        />
      )}
    </section>
  );
};
